import { ArrowRight, FileText, Sparkles, Star, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { EvidenceSourceChip } from '@/components/evidence/EvidenceSourceChip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { GraphViewNode } from '@/types/graph-view'
import type { Investigation } from '@/types/investigation'
import { cn } from '@/utils/cn'
import { entityColorClass } from './previews/entity-color'
import { GraphMiniPreview } from './previews/graph-mini-preview'
import { MapMiniPreview } from './previews/map-mini-preview'
import { TimelineMiniPreview } from './previews/timeline-mini-preview'

interface InvestigationDigestCardProps {
  investigation: Investigation
  caseId: string
  index: number
}

interface NodeBucket {
  bucket: 'persons' | 'documents' | 'entities'
  displayName: string
  identifier: string | null
  label: string | null
}

const PERSON_LABELS = new Set(['Person'])
const DOCUMENT_LABELS = new Set(['Document', 'CaseDocument'])
const SKIP_LABELS = new Set([
  '__Entity__',
  '__KGBuilder__',
  'Chunk',
  'LogEvent',
  'CaseInsight',
  'CaseAnalysisMeta',
  'Hypothetical',
  'DailySummary',
  'OffchainEvent',
])

const NAME_KEYS = [
  'name',
  'original_name',
  'full_name',
  'legal_name',
  'filename',
  'title',
  'label',
  'vessel_name',
  'callsign',
  'e164',
  'msisdn',
  'iban',
  'address',
  'txid',
  'handle',
]
const IDENT_KEYS = [
  'e164',
  'msisdn',
  'iban',
  'imei',
  'device_id',
  'license_plate',
  'spz',
  'reg_number',
  'personal_number',
  'document_number',
  'badge_id',
  'tail_number',
  'icao',
  'mmsi',
  'imo',
  'address',
  'txid',
  'handle',
]

// Stacked (below sm) an empty preview is a slim strip, not a blank 16:11 panel.
const PREVIEW_CLASS = 'aspect-[16/11] max-sm:data-empty:aspect-auto max-sm:data-empty:h-20'

export function InvestigationDigestCard({
  investigation,
  caseId,
  index,
}: InvestigationDigestCardProps) {
  const { t } = useTranslation()
  const { nodes, edges } = investigation.graphView
  const buckets = splitNodes(nodes)
  const persons = buckets.filter(b => b.bucket === 'persons').slice(0, 6)
  const documents = buckets.filter(b => b.bucket === 'documents').slice(0, 4)
  const entities = buckets.filter(b => b.bucket === 'entities').slice(0, 6)
  const firstNote = investigation.notes[0]
  const updated = new Date(investigation.updatedAt).toLocaleString()

  return (
    <Card size="compact" className="overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-border/40 px-4 py-4 sm:flex-row sm:items-start sm:justify-between md:px-5">
        <div className="flex min-w-0 items-start gap-3">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 font-mono text-xs font-semibold text-primary">
            № {String(index).padStart(2, '0')}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground break-words md:truncate">
                {investigation.title || t('cases.investigationDigest.untitled')}
              </h3>
              <Badge variant={investigation.source === 'ai' ? 'info' : 'muted'} className="gap-1">
                {investigation.source === 'ai' ? (
                  <Sparkles className="size-3" />
                ) : (
                  <User className="size-3" />
                )}
                {investigation.source}
              </Badge>
              {investigation.includedInReport && (
                <Badge variant="warning" className="gap-1">
                  <Star className="size-3" />
                  {t('cases.investigationDigest.inReport')}
                </Badge>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {investigation.authorName} ·{' '}
              {t('cases.investigationDigest.updated', { date: updated })} ·{' '}
              <span className="font-mono">
                {nodes.length}n / {edges.length}e
              </span>
            </p>
          </div>
        </div>
        <Button asChild variant="outline" size="sm" className="h-9 shrink-0 gap-1.5 md:h-8">
          <Link to={`/cases/${caseId}/geolocation?investigation=${investigation.id}`}>
            {t('common.open')}
            <ArrowRight className="size-3.5" />
          </Link>
        </Button>
      </div>

      <div className="space-y-4 px-4 py-4 md:px-5">
        {firstNote ? (
          <p className="text-sm leading-relaxed text-foreground">{truncate(firstNote.text, 280)}</p>
        ) : (
          <p className="text-sm italic text-muted-foreground">
            {t('cases.investigationDigest.noNotes')}
          </p>
        )}

        <div className="grid gap-2 sm:grid-cols-3">
          <GraphMiniPreview nodes={nodes} edges={edges} className={PREVIEW_CLASS} />
          <TimelineMiniPreview nodes={nodes} className={PREVIEW_CLASS} />
          <MapMiniPreview nodes={nodes} className={PREVIEW_CLASS} />
        </div>

        {(persons.length > 0 || documents.length > 0 || entities.length > 0) && (
          <div className="grid grid-cols-1 gap-3 border-t border-border/30 pt-3 lg:grid-cols-3">
            <DigestList
              title={t('cases.investigationDigest.personsMentioned')}
              items={persons}
              showTypeChip={false}
            />
            <DigestList
              title={t('cases.investigationDigest.relatedDocuments')}
              items={documents}
              showTypeChip={false}
            />
            <DigestList
              title={t('cases.investigationDigest.otherEntities')}
              items={entities}
              showTypeChip
            />
          </div>
        )}

        {/* Research-snapshot evidence — agent citations grounded in
            documents or graph entities. Populated when the investigation
            was promoted from a hypothesis run or when its evidence has
            been regenerated. Skipped silently when empty. */}
        {investigation.evidence && investigation.evidence.length > 0 && (
          <div className="border-t border-border/30 pt-3">
            <p className="mb-2 text-3xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('cases.investigationDigest.evidence', { count: investigation.evidence.length })}
            </p>
            <ul className="space-y-2">
              {investigation.evidence.map((ev, i) => (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: evidence list is append-only
                  key={i}
                  className="flex flex-col gap-1.5 rounded-md border border-border/40 bg-muted/20 px-3 py-2"
                >
                  <span className="text-xs leading-snug text-foreground">{ev.text}</span>
                  {ev.source && <EvidenceSourceChip source={ev.source} />}
                </li>
              ))}
            </ul>
          </div>
        )}

        {investigation.notes.length > 0 && (
          <div className="border-t border-border/30 pt-3">
            <p className="mb-2 text-3xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('cases.investigationDigest.notes', { count: investigation.notes.length })}
            </p>
            <div className="space-y-2">
              {investigation.notes.map(note => (
                <div
                  key={note.id}
                  className="rounded-md border-l-2 border-primary bg-muted/30 px-3 py-2"
                >
                  <p className="text-2xs text-muted-foreground">
                    <span className="font-medium text-foreground">{note.authorName}</span> ·{' '}
                    {new Date(note.createdAt).toLocaleString()}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-foreground">{note.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

interface DigestListProps {
  title: string
  items: NodeBucket[]
  showTypeChip: boolean
}

function DigestList({ title, items, showTypeChip }: DigestListProps) {
  if (items.length === 0) return null
  return (
    <div>
      <p className="mb-1.5 text-3xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={`${item.displayName}-${i}`} className="flex items-baseline gap-2 text-xs">
            <FileText
              className={cn('size-2.5 shrink-0', entityColorClass(item.label))}
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate text-foreground">{item.displayName}</span>
            {showTypeChip && item.label && (
              <span className="shrink-0 rounded bg-muted px-1.5 font-mono text-3xs text-muted-foreground">
                {item.label}
              </span>
            )}
            {item.identifier && (
              <span className="max-w-[45%] shrink-0 truncate font-mono text-3xs text-muted-foreground md:max-w-none">
                {item.identifier}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function splitNodes(nodes: GraphViewNode[]): NodeBucket[] {
  const seen = new Map<string, true>()
  const buckets: NodeBucket[] = []
  for (const node of nodes) {
    const labels = new Set(node.labels ?? [])
    const primary = pickPrimaryLabel(node)
    const displayName = pickDisplayName(node)
    if (!displayName) continue
    let bucket: NodeBucket['bucket']
    if ([...labels].some(l => PERSON_LABELS.has(l))) bucket = 'persons'
    else if ([...labels].some(l => DOCUMENT_LABELS.has(l))) bucket = 'documents'
    else if (primary && !SKIP_LABELS.has(primary)) bucket = 'entities'
    else continue
    const identifier = pickIdentifier(node)
    const key = `${bucket}:${displayName}:${identifier ?? ''}`
    if (seen.has(key)) continue
    seen.set(key, true)
    buckets.push({ bucket, displayName, identifier, label: primary })
  }
  return buckets
}

function pickDisplayName(node: GraphViewNode): string | null {
  if (node.name) return node.name
  const props = node.properties ?? {}
  for (const key of NAME_KEYS) {
    const value = props[key]
    if (typeof value === 'string' && value.trim()) return value
    if (typeof value === 'number') return String(value)
  }
  return null
}

function pickIdentifier(node: GraphViewNode): string | null {
  const props = node.properties ?? {}
  for (const key of IDENT_KEYS) {
    const value = props[key]
    if (typeof value === 'string' && value.trim()) return value
    if (typeof value === 'number') return String(value)
  }
  return null
}

function pickPrimaryLabel(node: GraphViewNode): string | null {
  if (node.typeLabel && !SKIP_LABELS.has(node.typeLabel)) return node.typeLabel
  for (const label of node.labels ?? []) {
    if (!SKIP_LABELS.has(label)) return label
  }
  return null
}

function truncate(value: string, max: number): string {
  if (!value) return ''
  if (value.length <= max) return value
  return value.slice(0, max - 1).trimEnd() + '…'
}
